import { useState, useEffect } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { shipmentMissingService } from "../../services/shipmentMissingService"
import { useAuth } from "../../context/AuthContext"
import Alert from "../common/Alert"
import { ArrowLeft, Pencil, AlertTriangle } from "lucide-react";
import { PageLayout, Button, StatusBadge, DetailCard, DetailRow } from "../common/index.js";

const ViewMissing = () => {
  const { missingId } = useParams()
  const navigate = useNavigate()
  const { user } = useAuth()
  const [loading, setLoading] = useState(true)
  const [missingData, setMissingData] = useState(null)

  useEffect(() => {
    fetchMissing()
  }, [missingId])

  const fetchMissing = async () => {
    try {
      setLoading(true)
      const response = await shipmentMissingService.getMissing(missingId)
      const missing = response.data.missing || response.data.data

      if (missing) {
        setMissingData(missing)
      } else {
        Alert.error("Missing shipment record not found")
      }
    } catch (error) {
      console.error("Error fetching missing:", error)
      Alert.error(error.response?.data?.error || "Failed to load missing shipment")
    } finally {
      setLoading(false)
    }
  }

  const getStatusBadgeColor = (status) => {
    const colors = {
      pending: "bg-yellow-100 text-yellow-800 border-yellow-200",
      approved: "bg-green-100 text-green-800 border-green-200",
      rejected: "bg-red-100 text-red-800 border-red-200",
    }
    return colors[status?.toLowerCase()] || "bg-gray-100 text-gray-800 border-gray-200"
  }

  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "pending":
        return "⏳"
      case "approved":
        return "✅"
      case "rejected":
        return "❌"
      default:
        return "📋"
    }
  }

  if (loading) {
    return (
      <PageLayout title="Missing Shipment Details" icon={AlertTriangle}>
        <div className="flex items-center justify-center py-16">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-brand mx-auto mb-4"></div>
          <p className="text-gray-600">Loading missing shipment...</p>
        </div>
      </PageLayout>
    )
  }

  if (!missingData) {
    return (
      <PageLayout title="Missing Shipment Details" icon={AlertTriangle}>
        <div className="flex items-center justify-center py-16">
          <Button onClick={() => navigate("/missing")}>Back to Missing</Button>
        </div>
      </PageLayout>
    )
  }

  return (
    <PageLayout
      title="Missing Shipment Details"
      icon={AlertTriangle}
      maxWidth="max-w-7xl"
      actions={
        <>
          <StatusBadge status={missingData.approval_status} />
          <Button variant="outline" onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}>
            View Shipment
          </Button>
          <Button variant="outline" icon={ArrowLeft} onClick={() => navigate('/missing')}>
            Back
          </Button>
        </>
      }
    >

                {/* Missing Information Card */}
                <div className="bg-white rounded-sm overflow-hidden mb-6">
                  <div className="px-4 sm:px-6 pt-4 pb-0">
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Missing Information
                    </h3>
                  </div>

                  <div className="p-4 sm:p-6 sm:pt-0">
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-8">
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Missing ID</p>
                        <p className="font-semibold text-tertiary text-sm">#{missingData.missing_id}</p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Shipment ID</p>
                        <button
                          onClick={() => navigate(`/shipments/view/${missingData.shipment_id}`)}
                          className="font-semibold text-tertiary text-sm hover:underline"
                        >
                          #{missingData.shipment_id}
                        </button>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Created By</p>
                        <p className="font-semibold text-tertiary text-sm">{missingData.created_by || "N/A"}</p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Quantity Missing</p>
                        <p className="font-semibold text-tertiary text-sm">{missingData.quantity_missing || "N/A"}</p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Created Date</p>
                        <p className="font-semibold text-tertiary text-sm">
                          {missingData.created_at
                            ? new Date(missingData.created_at).toLocaleString("en-IN", {
                                day: "2-digit",
                                month: "short",
                                year: "numeric",
                                hour: "2-digit",
                                minute: "2-digit",
                              })
                            : "N/A"}
                        </p>
                      </div>
                      <div className="p-0">
                        <p className="text-xs sm:text-sm text-gray-600 font-semibold mb-1 sm:mb-2">Status</p>
                        <span
                          className={`inline-block px-3 py-1 rounded-full text-xs font-semibold uppercase ${getStatusBadgeColor(missingData.approval_status).replace("border-", "")}`}
                        >
                          {missingData.approval_status}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Reason for Missing */}
                <div className="bg-white rounded-sm overflow-hidden mb-6">
                  <div className="px-4 sm:px-6 pt-4 pb-0">
                    <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                      Reason for Missing
                    </h3>
                  </div>
                  <div className="p-4 sm:p-6 sm:pt-0">
                    <div className="p-0">
                      <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{missingData.reason}</p>
                    </div>
                  </div>
                </div>

                {/* Missing Details (if provided) */}
                {missingData.missing_details && (
                  <div className="bg-white rounded-sm overflow-hidden mb-6">
                    <div className="px-4 sm:px-6 pt-4 pb-0">
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">Additional Details</h3>
                    </div>
                    <div className="p-4 sm:p-6 sm:pt-0">
                      <div className="p-0">
                        <p className="text-gray-900 whitespace-pre-wrap leading-relaxed">{missingData.missing_details}</p>
                      </div>
                    </div>
                  </div>
                )}

                {/* Rejection Reason (if rejected) */}
                {missingData.rejection_reason && (
                  <div className="bg-white rounded-sm overflow-hidden mb-6">
                    <div className="px-4 sm:px-6 pt-4 pb-0">
                      <h3 className="text-base sm:text-md font-semibold text-secondary mb-3 sm:mb-4 pb-2 border-b-2 border-grey-400">
                        
                        Rejection Reason
                      </h3>
                    </div>
                    <div className="p-4 sm:p-6 sm:pt-0">
                      <div className="p-0">
                        <p className="text-red-900 whitespace-pre-wrap leading-relaxed">{missingData.rejection_reason}</p>
                      </div>
                    </div>
                  </div>
                )}
    </PageLayout>
  )
}

export default ViewMissing;